/**
 * Mirror of `ExerciseGroupType` enum in be-coaching
 * (src/exercise/entities/exercise-group-type.enum.ts).
 * Default is `STANDARD`. Backwards compat: `isRestPeriod` boolean stays in
 * sync with `groupType === REST` on writes; readers can use either.
 */
export enum ExerciseGroupType {
  STANDARD = 'standard',
  REST = 'rest',
  SUPERSET = 'superset',
  CIRCUIT = 'circuit',
  TABATA = 'tabata',
  EMOM = 'emom',
  AMRAP = 'amrap'
}

/**
 * Per-type config payload. Only the relevant fields are populated per type.
 *  - TABATA: workSeconds, restSeconds, rounds
 *  - EMOM: intervalSeconds, totalRounds
 *  - AMRAP: totalSeconds
 */
export interface IGroupConfig {
  workSeconds?: number | null;
  restSeconds?: number | null;
  rounds?: number | null;
  intervalSeconds?: number | null;
  totalRounds?: number | null;
  totalSeconds?: number | null;
}

/**
 * Display label for a group type, localized via the React Intl id pattern
 * `editor.groupType.<value>`. Caller resolves with `useIntl().formatMessage`.
 */
export const GROUP_TYPE_INTL_ID: Record<ExerciseGroupType, string> = {
  [ExerciseGroupType.STANDARD]: 'editor.groupType.standard',
  [ExerciseGroupType.REST]: 'editor.groupType.rest',
  [ExerciseGroupType.SUPERSET]: 'editor.groupType.superset',
  [ExerciseGroupType.CIRCUIT]: 'editor.groupType.circuit',
  [ExerciseGroupType.TABATA]: 'editor.groupType.tabata',
  [ExerciseGroupType.EMOM]: 'editor.groupType.emom',
  [ExerciseGroupType.AMRAP]: 'editor.groupType.amrap'
};

/**
 * Compact display string ("Tabata 20/10×2", "EMOM 60s×8", "AMRAP 600s") for
 * the plan viewer. Returns null when there's nothing extra to show.
 */
export function describeGroupConfig(
  type: ExerciseGroupType | string | null | undefined,
  config: IGroupConfig | null | undefined
): string | null {
  if (!config) return null;
  switch (type) {
    case ExerciseGroupType.TABATA: {
      const w = config.workSeconds ?? 0;
      const r = config.restSeconds ?? 0;
      const rounds = config.rounds ?? 0;
      if (w === 0 && r === 0 && rounds === 0) return null;
      return `${w}/${r}×${rounds}`;
    }
    case ExerciseGroupType.EMOM: {
      const i = config.intervalSeconds ?? 0;
      const r = config.totalRounds ?? 0;
      if (i === 0 && r === 0) return null;
      return `${i}s×${r}`;
    }
    case ExerciseGroupType.AMRAP: {
      const t = config.totalSeconds ?? 0;
      if (t === 0) return null;
      return `${t}s`;
    }
    default:
      return null;
  }
}
